import { useState, useEffect } from "react"
import { PlusIcon, TrashIcon, ChevronDownIcon } from "@heroicons/react/24/outline"
import Modal from "aldoo-ra/Modal"
import Button from "aldoo-ra/Button"
import Typography from "aldoo-ra/Typography"
import Input from "aldoo-ra/Input"
import Checkbox from "aldoo-ra/Checkbox"
import Tooltip from "aldoo-ra/Tooltip"
import { showNotification } from "aldoo-ra/Notification"
import { showPromptDialog } from "aldoo-ra/PromptDialog"
import { api } from "aldoo-ra/API"

const crudAPI = api("crud")

const emptyForm = { groupId: "", name: "", allowMultiple: false, description: "" }

export default function ManageGroupsDialog({ isOpen, onClose }) {
  const [groups, setGroups] = useState([])
  const [loading, setLoading] = useState(false)
  const [form, setForm] = useState(emptyForm)
  const [saving, setSaving] = useState(false)
  const [expandedId, setExpandedId] = useState(null)
  const [showForm, setShowForm] = useState(false)

  const fetchGroups = async () => {
    setLoading(true)
    try {
      const res = await crudAPI({
        get: {
          model: "ProductSubscriptionGroup",
          match: { deleted: { $ne: true } },
          sort: { name: 1 }
        }
      })
      setGroups(Array.isArray(res) ? res : (res?.data ?? []))
    } catch (e) {
      console.error("Failed to fetch groups:", e)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    if (isOpen) {
      fetchGroups()
      setForm(emptyForm)
      setShowForm(false)
    }
  }, [isOpen])

  const handleAdd = async () => {
    if (!form.groupId.trim()) {
      showNotification({ message: "Group ID is required", className: "bg-admin_error text-admin_text_inv" })
      return
    }
    if (!form.name.trim()) {
      showNotification({ message: "Name is required", className: "bg-admin_error text-admin_text_inv" })
      return
    }

    setSaving(true)
    try {
      await crudAPI({
        create: {
          model: "ProductSubscriptionGroup",
          data: {
            groupId: form.groupId.trim().toLowerCase().replace(/\s+/g, "-"),
            name: form.name.trim(),
            allowMultiple: form.allowMultiple,
            description: form.description.trim(),
          }
        }
      })
      showNotification({ message: "Group created", className: "bg-admin_primary text-admin_text_inv" })
      setForm(emptyForm)
      setShowForm(false)
      fetchGroups()
    } catch (e) {
      showNotification({ message: "Failed to create group", className: "bg-admin_error text-admin_text_inv" })
    } finally {
      setSaving(false)
    }
  }

  const handleToggleAllowMultiple = async (group, value) => {
    setGroups((prev) => prev.map((g) => g._id === group._id ? { ...g, allowMultiple: value } : g))
    try {
      await crudAPI({
        update: {
          model: "ProductSubscriptionGroup",
          data: { _id: group._id, allowMultiple: value }
        }
      })
    } catch (e) {
      setGroups((prev) => prev.map((g) => g._id === group._id ? { ...g, allowMultiple: group.allowMultiple } : g))
      showNotification({ message: "Failed to update group", className: "bg-admin_error text-admin_text_inv" })
    }
  }

  const handleDelete = (group) => {
    showPromptDialog({
      variant: "admin",
      title: "Delete Group",
      message: `Delete "${group.name}"? Products assigned to this group will lose their group assignment.`,
      confirmText: "Delete",
      onConfirm: async () => {
        try {
          await crudAPI({
            delete: {
              model: "ProductSubscriptionGroup",
              mode: "soft",
              match: { _id: group._id }
            }
          })
          showNotification({ message: "Group deleted", className: "bg-admin_primary text-admin_text_inv" })
          fetchGroups()
        } catch (e) {
          showNotification({ message: "Failed to delete group", className: "bg-admin_error text-admin_text_inv" })
        }
      }
    })
  }

  return (
    <Modal 
      isOpen={isOpen} 
      onClose={onClose} 
      variant="admin_compact" 
      className="w-full sm:w-[560px]"
    >
      <div className="flex flex-col gap-6 px-6 py-4">
        {/* Title + Add button */}
        <div className="flex items-center justify-between">
          <Typography variant="admin_h4">
            Subscription Groups
          </Typography>

          <Button 
            variant="admin_primary" 
            onClick={() => { setShowForm(true); setForm(emptyForm) }} 
            disabled={showForm}
          >
            <PlusIcon className="w-4 h-4" />
            Add Group
          </Button>
        </div>

        {/* Collapsible create form */}
        {showForm && (
          <div className="rounded-xl border border-admin_bordered bg-admin_paper p-4 flex flex-col gap-3">
            <div className="grid grid-cols-2 gap-3">
              <div className="flex flex-col gap-1">
                <Typography variant="admin_label_sm_dim">
                  Group ID *
                </Typography>
                <Input
                  variant="admin"
                  value={form.groupId}
                  onChange={(e) => setForm((f) => ({ ...f, groupId: e.target.value }))}
                  placeholder="e.g. product-plans"
                />
              </div>

              <div className="flex flex-col gap-1">
                <Typography variant="admin_label_sm_dim">
                  Name *
                </Typography>
                <Input
                  variant="admin"
                  value={form.name}
                  onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                  placeholder="e.g. Product Plans"
                />
              </div>
            </div>

            <div className="flex flex-col gap-1">
              <Typography variant="admin_label_sm_dim">
                Description
              </Typography>
              <Input
                variant="admin"
                value={form.description}
                onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
                placeholder="Optional description"
              />
            </div>

            <div className="flex items-center gap-2 cursor-pointer select-none">
              <Checkbox
                variant="admin_compact"
                isOn={form.allowMultiple}
                onChange={(value) => setForm((f) => ({ ...f, allowMultiple: value }))}
              />
              <Typography 
                variant="admin_txt1" 
                className="text-admin_text"
              >
                Allow multiple active subscriptions in this group
              </Typography>
            </div>

            <div className="flex items-center justify-end gap-2">
              <Button 
                variant="admin_secondary" 
                onClick={() => { setShowForm(false); setForm(emptyForm) }}
              >
                Cancel
              </Button>
              <Button 
                variant="admin_primary" 
                onClick={handleAdd} disabled={saving}
              >
                Create
              </Button>
            </div>
          </div>
        )}

        {/* Groups list */}
        <div className="flex flex-col gap-2">
          {loading ? (
            <div className="h-16 rounded-xl bg-admin_neutral animate-pulse" />
          ) : groups.length === 0 ? (
            <Typography 
              variant="admin_body1_dim" 
              className="text-center"
            >
              No groups yet
            </Typography>
          ) : (
            groups.map((g) => {
              const expanded = expandedId === g._id
              return (
                <div
                  key={g._id}
                  className="rounded-xl border border-admin_bordered overflow-hidden"
                >
                  <button
                    className="w-full flex items-center justify-between gap-3 px-4 py-3 text-left bg-admin_black hover:brightness-125 transition"
                    onClick={() => setExpandedId(expanded ? null : g._id)}
                  >
                    <div className="flex items-center gap-2 min-w-0">
                      <span className="font-semibold text-sm text-admin_text_inv truncate">
                        {g.name}
                      </span>
                      <span className="text-sm text-admin_neutral_2 font-mono shrink-0">
                        {g.groupId}
                      </span>
                    </div>

                    <div className="flex items-center gap-2 shrink-0">
                      <span className={`text-sm font-medium ${g.allowMultiple ? "text-admin_success" : "text-admin_warning"}`}>
                        {g.allowMultiple ? "Multiple" : "Single"}
                      </span>
                      <ChevronDownIcon className={`w-5 h-5 text-admin_neutral_2 transition-transform ${expanded ? "rotate-180" : ""}`} />
                    </div>
                  </button>

                  {expanded && (
                    <div className="px-4 pb-3 pt-1 bg-admin_paper border-t border-admin_bordered flex items-center justify-between gap-4">
                      <div className="flex items-center gap-2 cursor-pointer select-none">
                        <Checkbox
                          variant="admin_compact"
                          isOn={g.allowMultiple}
                          onChange={(value) => handleToggleAllowMultiple(g, value)}
                        />
                        <Typography
                          variant="admin_txt1" 
                          className="text-admin_text"
                        >
                          Allow multiple active subscriptions
                        </Typography>
                      </div>
                      <Tooltip text="Delete group">
                        <Button
                          variant="admin_icon"
                          className="text-admin_text_secondary hover:text-admin_error shrink-0"
                          onClick={() => handleDelete(g)}
                          aria-label="Delete"
                        >
                          <TrashIcon className="w-4 h-4" />
                        </Button>
                      </Tooltip>
                    </div>
                  )}
                </div>
              )
            })
          )}
        </div>
      </div>
    </Modal>
  )
}
